import React, { useState, useEffect, useRef } from "react";
import {
  Table,
  Tag,
  Typography,
  Button,
  message,
  Card,
  Space,
  Tabs,
  Avatar,
  Drawer,
  Descriptions,
  Row,
  Col,
  List,
  Divider,
  Select,
  Input,
} from "antd";
import {
  TeamOutlined,
  ReloadOutlined,
  UserOutlined,
  HistoryOutlined,
  FileTextOutlined,
  SearchOutlined,
} from "@ant-design/icons";

const { Title, Text, Link, Paragraph } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;

// --- CONFIGURATION ---
const API_URL = "https://service.ireedindia.com/v1/users/search";
const EMPLOYER_API_URL = "https://service.ireedindia.com/v1/get-employer?pageNumber=0&size=10000";

const IMAGE_BASE_URL = "https://service.ireedindia.com/image/";

interface StudentJobTracking {
  _id: string;
  applicationId: string;
  jobId: string;
  employerId: string;
  jobDetails: {
    title: string;
    company: string;
    location: string;
    department?: string;
    salary: string;
    employmentType: string;
  };
  employerDetails: {
    name: string;
    imageName: string;
    slug: string;
  };
  applicant: {
    name: string;
    email: string;
    mobile: string;
    role: string;
  };
  currentStatus: string;
  formSubmittedAt: string;
  sourceType: string;
  resumeFileName?: string;
  resumeUploadedAt?: string;
  timeline: Array<{ status: string; at: string }>;
}

interface StudentUser {
  _id: string;
  name: string;
  emailId: string;
  mobile: string;
  role: string;
  isVerified: boolean;
  workStatus?: string;
  availabilityToJoin?: string;
  resumeHeadline?: string;
  profileSummary?: string;
  keySkills?: string[];
  employment?: Array<{
    company: string;
    role: string;
    startDate: string;
    endDate?: string | null;
    description: string;
  }>;
  education?: Array<{
    qualification: string;
    boardOrUniversity: string;
    year: string;
    details: string;
  }>;
  projects?: Array<{
    title: string;
    description: string;
    startDate?: string;
    endDate?: string | null;
    link?: string;
  }>;
  languages?: Array<{
    language: string;
    proficiency: string;
    read: boolean;
    write: boolean;
    speak: boolean;
  }>;
  careerProfile?: {
    industry: string;
    department: string;
    roleCategory: string;
    jobRole: string;
  };
  personalDetails?: {
    gender: string;
    dob: string;
    category: string;
    differentlyAbled: boolean;
    careerBreak: boolean;
  };
  desiredEmploymentType?: string;
  desiredJobType?: string;
  expectedSalary?: string;
  preferredShift?: string;
  preferredWorkLocation?: string;
  jobTracking?: StudentJobTracking[];
  jobTrackingSummary?: {
    totalApplied: number;
    lastAppliedAt: string;
    applied: number;
  };
  latestJobApplication?: {
    applicationId: string;
    jobTitle: string;
    employerName: string;
    currentStatus: string;
    daysSinceApplied: number;
    appliedAt: string;
  };
  appliedDepartments?: string[];
  createdAt: string;
}

const AudienceForm: React.FC = () => {
  const [studentsData, setStudentsData] = useState<StudentUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Drawer state for individual student tracking
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<StudentUser | null>(null);
  const [drawerTab, setDrawerTab] = useState("summary");

  // Pagination State
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 40,
    total: 0
  });

  // Student search filters
  const [studentNameInput, setStudentNameInput] = useState("");
  const [studentName, setStudentName] = useState("");
  const [studentWorkStatus, setStudentWorkStatus] = useState<string | undefined>(undefined);
  const [studentDepartment, setStudentDepartment] = useState<string | undefined>(undefined);
  const [selectedEmployerName, setSelectedEmployerName] = useState<string | undefined>(undefined);
  const [employers, setEmployers] = useState<{ _id: string; name: string }[]>([]);
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const getAuthToken = () => {
    const storedDetails = localStorage.getItem("userDetails");
    return storedDetails ? JSON.parse(storedDetails)?.accessToken : null;
  };

  const fetchEmployers = async () => {
    try {
      const response = await fetch(EMPLOYER_API_URL);
      const result = await response.json();
      if (Array.isArray(result.data)) {
        setEmployers(result.data.map((e: any) => ({ _id: e._id, name: e.name })));
      }
    } catch (err) {
      console.error("Failed to fetch employers:", err);
    }
  };

  const fetchAudience = async (
    page: number,
    pageSize: number,
    name: string,
    workStatus: string | undefined,
    department: string | undefined,
    employerName: string | undefined
  ) => {
    setLoading(true);
    setError(null);
    const token = getAuthToken();

    if (!token) {
      setError("No authentication token found. Please log in again.");
      setLoading(false);
      return;
    }

    try {
      const body: Record<string, any> = {
        pageNumber: (page - 1).toString(),
        size: pageSize.toString(),
        role: ["student"],
      };
      if (name.trim()) body.name = name.trim();
      if (workStatus) body.workStatus = workStatus;
      if (department) body.department = department;
      if (employerName) body.employerName = employerName;

      const response = await fetch(API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      let students: StudentUser[] = [];
      let totalUsers = 0;

      if (result.data) {
        students = Array.isArray(result.data) ? result.data : (result.data.content || []);
        totalUsers = result.totalItems || result.totalElements || result.total || students.length;
      } else if (Array.isArray(result)) {
        students = result;
        totalUsers = result.length;
      }

      setStudentsData(students);
      setPagination(prev => ({
        ...prev,
        current: page,
        pageSize: pageSize,
        total: totalUsers
      }));

    } catch (err: any) {
      setError(err.message || "Failed to fetch audience data");
      message.error(err.message || "Failed to fetch audience data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployers();
  }, []);

  useEffect(() => {
    fetchAudience(1, pagination.pageSize, studentName, studentWorkStatus, studentDepartment, selectedEmployerName);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentName]);

  const studentColumns = [
    {
      title: "S.No",
      key: "sno",
      width: 60,
      // fixed: "left" as const,
      render: (_: any, __: any, index: number) => (
        <Text>{(pagination.current - 1) * pagination.pageSize + index + 1}</Text>
      ),
    },
    {
      title: "Student Name",
      dataIndex: "name",
      // width: 220,
      // fixed: "left" as const,
      render: (text: string, record: StudentUser) => (
        <Space>
          <Avatar icon={<UserOutlined />} src={undefined} />
          <Space direction="vertical" size={0}>
            <Text strong>{text}</Text>
            <Text type="secondary" style={{ fontSize: '12px' }}>{record.emailId}</Text>
          </Space>
        </Space>
      ),
    },
    {
      title: "Mobile",
      dataIndex: "mobile",
    },
    {
      title: "Work Status",
      dataIndex: "workStatus",
      filters: [
        { text: "Fresher", value: "Fresher" },
        { text: "Experienced", value: "Experienced" },
      ],
      filteredValue: studentWorkStatus ? [studentWorkStatus] : null,
      render: (status: string) => <Tag color="purple">{status?.toUpperCase() || "FRESHER"}</Tag>,
    },
    {
      title: "Department",
      key: "department",
      dataIndex: ["careerProfile", "department"],
      filters: [
        { text: "Sales & Business Development", value: "Sales & Business Development" },
        { text: "Marketing & Communication", value: "Marketing & Communication" },
        { text: "Finance & Accounts", value: "Finance & Accounts" },
        { text: "Legal & Compliance", value: "Legal & Compliance" },
        { text: "Projects & Construction", value: "Projects & Construction" },
        { text: "IT/ ITES", value: "IT/ ITES" },
        { text: "Human Resource", value: "Human Resource" },
        { text: "Research, Analysis & Strategy", value: "Research, Analysis & Strategy" },
        { text: "Customer Relationship Management", value: "Customer Relationship Management" },
        { text: "Hospitality & Facility Management", value: "Hospitality & Facility Management" },
        { text: "Corporate Social Responsibility", value: "Corporate Social Responsibility" },
      ],
      filterSearch: true,
      filteredValue: studentDepartment ? [studentDepartment] : null,
      render: (dept: string) => <Tag color="cyan">{dept || "N/A"}</Tag>,
    },
    {
      title: "Builder's Last Application",
      key: "latestEmployer",
      // width: 200,
      filters: employers.map(e => ({ text: e.name, value: e.name })),
      filterSearch: true,
      filteredValue: selectedEmployerName ? [selectedEmployerName] : null,
      render: (_: any, record: any) => {
        const latest = record.latestJobApplication;
        if (!latest) return <Text type="secondary">N/A</Text>;
        return (
          <Space direction="vertical" size={0} style={{ maxWidth: 180 }}>
            <Text strong style={{ fontSize: 12 }} ellipsis={{ tooltip: latest.employerName }}>{latest.employerName}</Text>
            <Text type="secondary" style={{ fontSize: 11 }} ellipsis={{ tooltip: latest.jobTitle }}>{latest.jobTitle}</Text>
          </Space>
        );
      },
    },
    {
      title: "Applications",
      key: "appsCount",
      width: 110,
      fixed: "right" as const,
      render: (_: any, record: StudentUser) => (
        <Tag
          color="blue"
          style={{ cursor: 'pointer' }}
          onClick={() => {
            setSelectedStudent(record);
            setDrawerTab("tracking");
            setDrawerVisible(true);
          }}
        >
          {record.jobTrackingSummary?.totalApplied ?? record.jobTracking?.length ?? 0} Jobs
        </Tag>
      ),
    }
    // {
    //   title: "Actions",
    //   key: "actions",
    //   width: 130,
    //   fixed: "right" as const,
    //   render: (_: any, record: StudentUser) => (
    //     <Button
    //       type="link"
    //       icon={<HistoryOutlined />}
    //       onClick={() => {
    //         setSelectedStudent(record);
    //         setDrawerVisible(true);
    //       }}
    //     >
    //       View Details
    //     </Button>
    //   ),
    // },
  ];

  return (
    <div style={{ padding: "0px" }}>
      <Card bordered={false} style={{ borderRadius: '12px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', marginBottom: 10 }}>
          <Title level={2} style={{ margin: 0, display: 'flex', alignItems: 'center' }}>
            Audience Tracking
            <TeamOutlined style={{ marginLeft: 12, color: '#1890ff' }} />
          </Title>
          <Text type="secondary">Manage students and monitor their job application progress.</Text>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Space wrap>
            <Input
              placeholder="Search by name..."
              prefix={<SearchOutlined />}
              value={studentNameInput}
              allowClear
              style={{ width: 240 }}
              onChange={(e) => {
                const val = e.target.value;
                setStudentNameInput(val);
                if (searchTimeout.current) clearTimeout(searchTimeout.current);
                searchTimeout.current = setTimeout(() => {
                  setStudentName(val);
                  setPagination(prev => ({ ...prev, current: 1 }));
                }, 600);
              }}
            />
          </Space>
          <Button icon={<ReloadOutlined />} onClick={() => fetchAudience(1, pagination.pageSize, studentName, studentWorkStatus, studentDepartment, selectedEmployerName)}>
            Refresh
          </Button>
        </div>

        <Table
          columns={studentColumns as any}
          dataSource={studentsData}
          rowKey="_id"
          loading={loading}
          scroll={{ x: 'max-content' }}
          onChange={(tablePagination, filters) => {
            const page = tablePagination.current || 1;
            const pageSize = tablePagination.pageSize || pagination.pageSize;

            const ws = (filters.workStatus && filters.workStatus.length > 0)
              ? filters.workStatus[0] as string
              : undefined;
            const dept = (filters.department && filters.department.length > 0)
              ? filters.department[0] as string
              : undefined;
            const empName = (filters.latestEmployer && filters.latestEmployer.length > 0)
              ? filters.latestEmployer[0] as string
              : undefined;

            setStudentWorkStatus(ws);
            setStudentDepartment(dept);
            setSelectedEmployerName(empName);

            fetchAudience(page, pageSize, studentName, ws, dept, empName);
          }}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            pageSizeOptions: ["20", "40", "100", "500", "1000"],
            showTotal: (total) => `Total ${total} students`,
            position: ["topRight", "bottomRight"]
          }}
          bordered
        />
      </Card>

      <Drawer
        title={<Title level={4} style={{ margin: 0 }}>Student Profile: {selectedStudent?.name}</Title>}
        width="85%"
        onClose={() => { setDrawerVisible(false); setDrawerTab("summary"); }}
        visible={drawerVisible}
        destroyOnClose
        bodyStyle={{ padding: '0 24px 24px' }}
      >
        {selectedStudent && (
          <Tabs activeKey={drawerTab} onChange={setDrawerTab} size="large">
               <TabPane tab="Tracking" key="tracking">
              <div style={{ marginTop: 12 }}>
                {/* Compact Application Overview */}
                <div style={{ background: '#f0f5ff', border: '1px solid #d6e4ff', borderRadius: 8, padding: '10px 16px', marginBottom: 12 }}>
                  <Descriptions
                    size="small"
                    column={{ xs: 1, sm: 2, md: 4 }}
                    style={{ marginBottom: 0 }}
                  >
                    <Descriptions.Item label={<Text type="secondary" style={{ fontSize: 12 }}>Total Applied</Text>}>
                      <Text strong style={{ color: '#1677ff' }}>{selectedStudent.jobTrackingSummary?.totalApplied ?? 0}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text type="secondary" style={{ fontSize: 12 }}>Last Applied</Text>}>
                      <Text strong style={{ fontSize: 12 }}>
                        {selectedStudent.jobTrackingSummary?.lastAppliedAt
                          ? new Date(selectedStudent.jobTrackingSummary.lastAppliedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                          : 'N/A'}
                      </Text>
                    </Descriptions.Item>
                    {selectedStudent.latestJobApplication && (<>
                      <Descriptions.Item label={<Text type="secondary" style={{ fontSize: 12 }}>Latest Job</Text>}>
                        <Text strong style={{ fontSize: 12 }}>{selectedStudent.latestJobApplication.jobTitle}</Text>
                        {' @ '}
                        <Text style={{ fontSize: 12 }}>{selectedStudent.latestJobApplication.employerName}</Text>
                      </Descriptions.Item>
                      <Descriptions.Item label={<Text type="secondary" style={{ fontSize: 12 }}>Status</Text>}>
                        <Space size={4}>
                          <Tag color="green" style={{ margin: 0 }}>{selectedStudent.latestJobApplication.currentStatus?.toUpperCase()}</Tag>
                          <Tag color={selectedStudent.latestJobApplication.daysSinceApplied <= 3 ? 'green' : 'default'} style={{ margin: 0 }}>
                            {selectedStudent.latestJobApplication.daysSinceApplied}d ago
                          </Tag>
                        </Space>
                      </Descriptions.Item>
                    </>)}
                    <Descriptions.Item label={<Text type="secondary" style={{ fontSize: 12 }}>Departments</Text>} span={4}>
                      {selectedStudent.appliedDepartments?.length
                        ? selectedStudent.appliedDepartments.map(d => <Tag key={d} color="cyan" style={{ marginBottom: 2 }}>{d}</Tag>)
                        : <Text type="secondary" style={{ fontSize: 12 }}>N/A</Text>}
                    </Descriptions.Item>
                  </Descriptions>
                </div>

                <Title level={5} style={{ margin: '0 0 8px 0' }}>Application History ({selectedStudent.jobTrackingSummary?.totalApplied ?? selectedStudent.jobTracking?.length ?? 0})</Title>
                <Table
                  dataSource={(selectedStudent.jobTracking || []).map((item, idx) => ({ ...item, _sno: idx + 1 }))}
                  rowKey="applicationId"
                  pagination={false}
                  bordered
                  scroll={{ x: 'max-content' }}
                  expandable={{
                    expandedRowRender: (item: any) => (
                      <div style={{ padding: '8px 16px', background: '#f9f9f9' }}>
                        <Text strong>Timeline</Text>
                        <div style={{ marginTop: 8 }}>
                          {item.timeline?.map((t: any, i: number) => (
                            <div key={i} style={{ marginBottom: 4 }}>
                              <Tag color="blue">{t.status}</Tag>
                              <Text type="secondary" style={{ fontSize: 12 }}>{new Date(t.at).toLocaleString()}</Text>
                            </div>
                          ))}
                        </div>
                      </div>
                    ),
                    rowExpandable: (item: any) => (item.timeline?.length || 0) > 0,
                  }}
                  columns={[
                    {
                      title: 'S.No',
                      dataIndex: '_sno',
                    },
                    {
                      title: 'Employer',
                      key: 'employer',
                      render: (_: any, item: any) => (
                        <Space>
                          {item.employerDetails?.imageName && (
                            <Avatar
                              src={`${IMAGE_BASE_URL}${item.employerDetails.imageName}`}
                              shape="square"
                              size="small"
                            />
                          )}
                          <Space direction="vertical" size={0}>
                            <Text strong>{item.employerDetails?.name || 'N/A'}</Text>
                            <Text type="secondary" style={{ fontSize: 11 }}>{item.employerDetails?.slug}</Text>
                          </Space>
                        </Space>
                      ),
                    },
                    {
                      title: 'Job Title',
                      key: 'jobTitle',
                      render: (_: any, item: any) => (
                        <Space direction="vertical" size={0}>
                          <Text strong>{item.jobDetails?.title}</Text>
                          <Text type="secondary" style={{ fontSize: 11 }}>{item.jobDetails?.company}</Text>
                        </Space>
                      ),
                    },
                    {
                      title: 'Department',
                      key: 'dept',
                      render: (_: any, item: any) => <Tag color="cyan">{item.jobDetails?.department || 'N/A'}</Tag>,
                    },
                    {
                      title: 'Location',
                      dataIndex: ['jobDetails', 'location'],
                    },
                    {
                      title: 'Salary',
                      dataIndex: ['jobDetails', 'salary'],
                    },
                    {
                      title: 'Employment Type',
                      key: 'empType',
                      render: (_: any, item: any) => <Tag color="geekblue">{item.jobDetails?.employmentType}</Tag>,
                    },
                    {
                      title: 'Source',
                      dataIndex: 'sourceType',
                      render: (s: string) => <Tag color="orange">{s}</Tag>,
                    },
                    {
                      title: 'Status',
                      dataIndex: 'currentStatus',
                      render: (s: string) => <Tag color="green">{s?.toUpperCase()}</Tag>,
                    },
                    {
                      title: 'Days Since Applied',
                      dataIndex: 'daysSinceApplied',
                      render: (d: number) => <Tag color={d === 0 ? 'green' : 'default'}>{d} day{d !== 1 ? 's' : ''}</Tag>,
                    },
                    {
                      title: 'Applied On',
                      dataIndex: 'formSubmittedAt',
                      render: (d: string) => d ? new Date(d).toLocaleString() : 'N/A',
                    },
                    {
                      title: 'Resume',
                      dataIndex: 'resumeFileName',
                      render: (fileName: string) =>
                        fileName ? (
                          <Link href={`${IMAGE_BASE_URL}${fileName}`} target="_blank">
                            <Tag color="purple" icon={<FileTextOutlined />} style={{ cursor: 'pointer' }}>{fileName}</Tag>
                          </Link>
                        ) : <Text type="secondary">N/A</Text>,
                    },
                    {
                      title: 'Resume Uploaded At',
                      dataIndex: 'resumeUploadedAt',
                      render: (d: string) => d ? new Date(d).toLocaleString() : 'N/A',
                    },
                  ] as any}
                />
              </div>
            </TabPane>
            <TabPane tab="Summary" key="summary">
              <Row gutter={[24, 24]} style={{ marginTop: 16 }}>
                <Col span={16}>
                  <Card title="Career Summary" bordered={false} className="shadow-sm">
                    <Title level={5}>{selectedStudent.resumeHeadline || "Professional"}</Title>
                    <Paragraph type="secondary">{selectedStudent.profileSummary || "No summary provided."}</Paragraph>
                    <Divider plain>Key Skills</Divider>
                    <Space size={[0, 8]} wrap>
                      {selectedStudent.keySkills?.map(skill => <Tag color="blue" key={skill}>{skill}</Tag>)}
                    </Space>
                  </Card>
                </Col>
                <Col span={8}>
                  <Card title="Contact Info" bordered={false} className="shadow-sm">
                    <Descriptions column={1} size="small">
                      <Descriptions.Item label="Email">{selectedStudent.emailId}</Descriptions.Item>
                      <Descriptions.Item label="Mobile">{selectedStudent.mobile}</Descriptions.Item>
                      <Descriptions.Item label="Work Status"><Tag color="purple">{selectedStudent.workStatus}</Tag></Descriptions.Item>
                      <Descriptions.Item label="Availability">{selectedStudent.availabilityToJoin}</Descriptions.Item>
                    </Descriptions>
                  </Card>
                </Col>
              </Row>
            </TabPane>

         

            <TabPane tab="Experience & Projects" key="exp">
              <Row gutter={[24, 24]} style={{ marginTop: 16 }}>
                <Col span={12}>
                  <Card title="Employment History" bordered={false} className="shadow-sm">
                    {selectedStudent.employment?.length ? (
                      <List
                        itemLayout="vertical"
                        dataSource={selectedStudent.employment}
                        renderItem={item => (
                          <List.Item>
                            <List.Item.Meta
                              title={<Text strong>{item.role} @ {item.company}</Text>}
                              description={<Text type="secondary">{item.startDate} - {item.endDate || 'Present'}</Text>}
                            />
                            <Paragraph style={{ fontSize: '13px' }}>{item.description}</Paragraph>
                          </List.Item>
                        )}
                      />
                    ) : <Text type="secondary">No employment data.</Text>}
                  </Card>
                </Col>
                <Col span={12}>
                  <Card title="Projects" bordered={false} className="shadow-sm">
                    {selectedStudent.projects?.length ? (
                      <List
                        itemLayout="vertical"
                        dataSource={selectedStudent.projects}
                        renderItem={item => (
                          <List.Item>
                            <List.Item.Meta
                              title={item.link ? <Link href={item.link} target="_blank">{item.title}</Link> : item.title}
                              description={item.startDate ? `${item.startDate} - ${item.endDate || 'N/A'}` : null}
                            />
                            <Paragraph style={{ fontSize: '13px' }}>{item.description}</Paragraph>
                          </List.Item>
                        )}
                      />
                    ) : <Text type="secondary">No projects data.</Text>}
                  </Card>
                </Col>
              </Row>
            </TabPane>

            <TabPane tab="Education & Languages" key="edu">
              <Row gutter={[24, 24]} style={{ marginTop: 16 }}>
                <Col span={12}>
                  <Card title="Education" bordered={false} className="shadow-sm">
                    {selectedStudent.education?.length ? (
                      <List
                        dataSource={selectedStudent.education}
                        renderItem={item => (
                          <List.Item>
                            <List.Item.Meta
                              title={<Text strong>{item.qualification}</Text>}
                              description={`${item.boardOrUniversity} (${item.year})`}
                            />
                            <Text type="secondary" style={{ fontSize: '12px' }}>{item.details}</Text>
                          </List.Item>
                        )}
                      />
                    ) : <Text type="secondary">No education data.</Text>}
                  </Card>
                </Col>
                <Col span={12}>
                  <Card title="Languages" bordered={false} className="shadow-sm">
                    {selectedStudent.languages?.length ? (
                      <List
                        dataSource={selectedStudent.languages}
                        renderItem={item => (
                          <List.Item>
                            <Space split={<Divider type="vertical" />}>
                              <Text strong>{item.language}</Text>
                              <Tag color="cyan">{item.proficiency}</Tag>
                              <Space size={4}>
                                {item.read && <Tag color="success">Read</Tag>}
                                {item.write && <Tag color="success">Write</Tag>}
                                {item.speak && <Tag color="success">Speak</Tag>}
                              </Space>
                            </Space>
                          </List.Item>
                        )}
                      />
                    ) : <Text type="secondary">No language data.</Text>}
                  </Card>
                </Col>
              </Row>
            </TabPane>

            <TabPane tab="Preferences & Details" key="pref">
              <Row gutter={[24, 24]} style={{ marginTop: 16 }}>
                <Col span={12}>
                  <Card title="Career Profile" bordered={false} className="shadow-sm">
                    <Descriptions column={1} size="small" bordered>
                      <Descriptions.Item label="Industry">{selectedStudent.careerProfile?.industry}</Descriptions.Item>
                      <Descriptions.Item label="Department">{selectedStudent.careerProfile?.department}</Descriptions.Item>
                      <Descriptions.Item label="Role">{selectedStudent.careerProfile?.jobRole}</Descriptions.Item>
                      <Descriptions.Item label="Category">{selectedStudent.careerProfile?.roleCategory}</Descriptions.Item>
                    </Descriptions>
                    <Divider plain>Desired Employment</Divider>
                    <Descriptions column={1} size="small" bordered>
                      <Descriptions.Item label="Job Type">{selectedStudent.desiredJobType}</Descriptions.Item>
                      <Descriptions.Item label="Employment">{selectedStudent.desiredEmploymentType}</Descriptions.Item>
                      <Descriptions.Item label="Expected Salary">{selectedStudent.expectedSalary}</Descriptions.Item>
                      <Descriptions.Item label="Location">{selectedStudent.preferredWorkLocation}</Descriptions.Item>
                    </Descriptions>
                  </Card>
                </Col>
                <Col span={12}>
                  <Card title="Personal Details" bordered={false} className="shadow-sm">
                    <Descriptions column={1} size="small" bordered>
                      <Descriptions.Item label="Gender">{selectedStudent.personalDetails?.gender}</Descriptions.Item>
                      <Descriptions.Item label="DOB">{selectedStudent.personalDetails?.dob ? new Date(selectedStudent.personalDetails.dob).toLocaleDateString() : 'N/A'}</Descriptions.Item>
                      <Descriptions.Item label="Category">{selectedStudent.personalDetails?.category}</Descriptions.Item>
                      <Descriptions.Item label="Differently Abled">{selectedStudent.personalDetails?.differentlyAbled ? 'Yes' : 'No'}</Descriptions.Item>
                      <Descriptions.Item label="Career Break">{selectedStudent.personalDetails?.careerBreak ? 'Yes' : 'No'}</Descriptions.Item>
                    </Descriptions>
                  </Card>
                </Col>
              </Row>
            </TabPane>
          </Tabs>
        )}
      </Drawer>
    </div>
  );
};

export default AudienceForm;
